import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { handleApiError, showSuccess } from '../utils/errorHandler';
import { useTranslation } from '../contexts/I18nContext';
import { Shield, LogIn } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function SuperAdminLoginPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, password: formData.password })
      });

      let data;
      try { data = await response.json(); } catch { data = {}; }

      if (response.ok) {
        localStorage.setItem('superAdminToken', data.token);
        localStorage.setItem('superAdmin', JSON.stringify(data.admin));
        showSuccess(t('auth.accessDone'));
        window.location.href = '/admin/dashboard';
      } else {
        handleApiError({ message: data.detail || t('auth.loginVerifyEmail') });
      }
    } catch (error) {
      handleApiError({ message: t('auth.connectionFailedNetwork') });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-slate-800/50 border-slate-700">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <CardTitle className="text-2xl text-white">{t('auth.superAdmin')}</CardTitle>
          <CardDescription className="text-slate-400">
            {t('auth.superAdminSubtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-slate-300">{t('auth.email')}</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="admin@tuodominio.com"
                data-testid="email-input"
              />
            </div>
            <div>
              <Label className="text-slate-300">{t('auth.password')}</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="bg-slate-700 border-slate-600 text-white"
                placeholder="••••••••"
                data-testid="password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? t('auth.loggingIn') : <><LogIn className="h-4 w-4 mr-2" /> {t('auth.login')}</>}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <a href="/" className="text-sm text-slate-500 hover:text-slate-400">
              {t('auth.backToUserLogin')}
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
